import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import AccountLayout from 'components/layout/drawer-page';
import { toaster } from 'components/toaster/toaster';
import { UserInfoForm } from 'components/user-info-form';
import {
  CommunicationPreferenceType,
  useUpdateUserCommunicationPreferencesMutation,
} from 'generated/graphql-gateway';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger, UserInfo } from 'state/auth/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { useUIContext } from 'state/ui';
import WebLocalStorage from 'utils/local-storage';
import { Keys } from 'utils/local-storage/constants';
import { routes } from 'utils/routing';

import { SMS_SUBSCRIPTION_EXP_TIME } from './comm-preferences-v2/comm-card-email-push';

export const AccountInfo: FC<React.PropsWithChildren<unknown>> = () => {
  const { formatMessage } = useIntl();
  const smsSubscriptionEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMS_MARKETING_SUBSCRIPTIONS);
  const [
    updateUserCommunicationPreferencesMutation,
  ] = useUpdateUserCommunicationPreferencesMutation();
  const { navigate } = useRouter();
  const { setUserEmail } = useUIContext();

  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const {
    closeModalAuth,
    setModalAuthState,
    signOut,
    user,
    updateUserInfo,
    setIsReAuthenticating,
    useUpdateMeMutationLoading,
  } = useAuthContext();

  const userInfo = useRef<UserInfo | null>(null);
  const [localUser, setLocalUser] = useState(user);

  const cleanup = useCallback(() => {
    setIsReAuthenticating(false);
    closeModalAuth();
  }, [closeModalAuth, setIsReAuthenticating]);

  useEffect(() => {
    if (user) {
      // Account for the scenario where the user attempts to access this component
      // without being authenticated, then authenticates and is redirected here.
      setLocalUser(user);
    }
  }, [user]);

  useEffectOnUnmount(() => {
    cleanup();
  });

  const handleOnUpdateUserSuccess = useCallback(() => {
    setIsReAuthenticating(false);
    navigate(routes.account);

    toaster.positive(formatMessage({ id: 'successfullyUpdatedUserAccountInfo' }));

    cleanup();
  }, [setIsReAuthenticating, navigate, formatMessage, cleanup]);

  const updateUser = useCallback(async () => {
    if (!userInfo.current) {
      return;
    }

    try {
      // Unsubscribe from SMS notifications when the phone number is updated
      const shouldUnsubscribeFromSMSNotifications =
        !!localUser?.details?.phoneNumber &&
        localUser.details.phoneNumber !== userInfo?.current.phoneNumber &&
        smsSubscriptionEnabled;

      if (shouldUnsubscribeFromSMSNotifications) {
        updateUserCommunicationPreferencesMutation({
          variables: {
            input: {
              communicationPreferenceType: CommunicationPreferenceType.MARKETINGSMS,
              newStatus: false,
            },
          },
        });

        WebLocalStorage.setItemWithExpiration(
          Keys.MARKETING_SMS_SUBSCRIPTION_REQUESTED,
          false,
          SMS_SUBSCRIPTION_EXP_TIME
        );
      }

      // TODO: Merge preference and details update into a single request
      // once we merge to apollo
      const updateUserOptions = { shouldThrowException: true };

      await updateUserInfo(userInfo.current, updateUserOptions);

      handleOnUpdateUserSuccess();
    } catch {
      setIsReAuthenticating(false);
    }
  }, [
    localUser,
    smsSubscriptionEnabled,
    updateUserInfo,
    handleOnUpdateUserSuccess,
    updateUserCommunicationPreferencesMutation,
    setIsReAuthenticating,
  ]);

  const signOutGlobally = useCallback(async () => {
    setIsReAuthenticating(true);

    await signOut(true);
  }, [signOut, setIsReAuthenticating]);

  const showModalAuth = useCallback(
    // This function is async as it needs tu return a Promise
    // as required by UserInfoForm's onSubmit prop.
    async (infoToUpdate: UserInfo) => {
      userInfo.current = infoToUpdate;

      setUserEmail(localUser?.details.email ?? ''); // Pre-populates user email in the sign-in form.

      setModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        trigger: ModalAuthTrigger.Global,
        disableEmailInput: true,
        heading: formatMessage({ id: 'pleaseReAuthenticateToContinue' }),
        onPreSignIn: signOutGlobally,
        onSignInSuccess: updateUser,
        shouldDisplaySignInSuccessMessage: false,
        shouldSkipDefaultSignInCallback: true,
      });
    },
    [
      setUserEmail,
      localUser?.details.email,
      setModalAuthState,
      formatMessage,
      signOutGlobally,
      updateUser,
    ]
  );

  return (
    <AccountLayout
      headerText={formatMessage({ id: 'account' })}
      helmetTitle={formatMessage({ id: 'accountDetails' })}
    >
      <UserInfoForm
        user={localUser}
        onSubmit={showModalAuth}
        readOnly={useUpdateMeMutationLoading}
      />
    </AccountLayout>
  );
};
